import { Component, OnDestroy, OnInit } from '@angular/core';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { Subscription } from 'rxjs';

import { notifyMessage } from '../../constants/notify-message';
import { Constants } from '../operator-settings-constants';
import { OperatorSettingsService } from '../operator-settings.service';
import { ZappiAbTestingService } from './zappi-ab-testing.service';
import {
  ZappiAbTestProductMarketConfig,
  ZappiAbTestRow,
  ZappiAbTestSkippedRow,
  ZappiAbTestSurveyConfig,
} from './zappi-ab-testing.interface';

@Component({
  selector: 'ps-zappi-ab-testing',
  templateUrl: './zappi-ab-testing.component.html',
  styleUrls: ['./zappi-ab-testing.component.css'],
})
export class ZappiAbTestingComponent implements OnInit, OnDestroy {
  private _subscriptions: Subscription = new Subscription();

  public enabled = false;
  public pendingEnabled = false;
  public loading = true;
  public configLoaded = false;
  public saving = false;

  public surveyConfigs: ZappiAbTestSurveyConfig[] = [];
  public productMarketConfigs: ZappiAbTestProductMarketConfig[] = [];
  public skippedRows: ZappiAbTestSkippedRow[] = [];

  constructor(
    private _zappiAbTestingService: ZappiAbTestingService,
    private _operatorSettingsService: OperatorSettingsService,
    private _toastr: ToasterService
  ) {}

  ngOnInit(): void {
    this._getConfig();
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  get combinedConfigs(): ZappiAbTestRow[] {
    const status = this.enabled ? 'Running' : 'Stopped';
    const surveyRows: ZappiAbTestRow[] = this.surveyConfigs.map((config) => ({
      type: 'Survey ID',
      configuration: `${config.surveyId}`,
      value: `${config.testCompleteCount}`,
      startedOn: config.startedOn || '-',
      status,
    }));
    const productRows: ZappiAbTestRow[] = this.productMarketConfigs.map(
      (config) => ({
        type: 'Product Code',
        configuration: `${config.productCode} (${config.market})`,
        value: `${config.testCompletePercent}%`,
        startedOn: config.startedOn || '-',
        status,
      })
    );
    return [...surveyRows, ...productRows];
  }

  uploadFile(event: Event) {
    const target = event.target as HTMLInputElement;
    const file: File = (target.files as FileList)[0];
    const isValidFile = this._operatorSettingsService.validateFileExtention(
      file,
      Constants.FILES_FILTER_LIST
    );
    if (!isValidFile) {
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    const upload$ = this._zappiAbTestingService.uploadCsv(formData).subscribe(
      (response) => {
        this.surveyConfigs = response.surveyConfigs || [];
        this.productMarketConfigs = response.productMarketConfigs || [];
        this.skippedRows = response.skippedRows || [];
        this._toastr.success('CSV processed. Configurations updated.');
      },
      (err) => {
        this._toastr.error(
          err?.error?.msg || notifyMessage.errorMessage.SERVER_ERROR
        );
      }
    );
    this._subscriptions.add(upload$);
  }

  save() {
    this.saving = true;
    const save$ = this._zappiAbTestingService
      .save({ enabled: this.pendingEnabled })
      .subscribe(
        (response) => {
          this.enabled = response.enabled;
          this.pendingEnabled = response.enabled;
          this.saving = false;
          this._toastr.success('Zappi A/B testing configuration saved');
        },
        (err) => {
          this.saving = false;
          this._toastr.error(
            err?.error?.msg || notifyMessage.errorMessage.SERVER_ERROR
          );
        }
      );
    this._subscriptions.add(save$);
  }

  private _getConfig() {
    const config$ = this._zappiAbTestingService.getConfig().subscribe(
      (response) => {
        this.enabled = response.enabled;
        this.pendingEnabled = response.enabled;
        this.surveyConfigs = response.surveyConfigs || [];
        this.productMarketConfigs = response.productMarketConfigs || [];
        this.configLoaded = true;
        this.loading = false;
      },
      (err) => {
        this._toastr.error(
          err?.error?.msg || notifyMessage.errorMessage.SERVER_ERROR
        );
        this.loading = false;
      }
    );
    this._subscriptions.add(config$);
  }
}
